// src/components/RevealCountdownBanner.js
import React, { useEffect, useMemo, useState } from "react";
import { Box, Typography, IconButton } from "@mui/material";
import CelebrationIcon from "@mui/icons-material/Celebration";
import ScheduleIcon from "@mui/icons-material/Schedule";
import CloseIcon from "@mui/icons-material/Close";

export default function RevealCountdownBanner({
  show,
  revealAtMs,
  message = "Surprise mode: you can only see your uploads until the reveal.",
}) {
  const [now, setNow] = useState(() => Date.now());
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!show) return;
    const id = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(id);
  }, [show]);

  const remainingMs = useMemo(() => {
    if (!show || !revealAtMs) return 0;
    return Math.max(0, Number(revealAtMs) - now);
  }, [show, revealAtMs, now]);

  if (!show || dismissed) return null;

  const totalSec = Math.floor(remainingMs / 1000);
  const days = Math.floor(totalSec / 86400);
  const hours = Math.floor((totalSec % 86400) / 3600);
  const mins = Math.floor((totalSec % 3600) / 60);
  const secs = totalSec % 60;

  const pad = (n) => String(n).padStart(2, "0");

  const topLabel = days > 0 ? `Reveal in ${days}d` : "Reveal in";
  const timeLabel = `${pad(hours)}:${pad(mins)}:${pad(secs)}`;

  return (
    <Box
      sx={{
        position: "fixed",
        top: "calc(env(safe-area-inset-top, 0px) + 12px)",
        left: "50%",
        transform: "translateX(-50%)",
        zIndex: 2200,

        width: "min(860px, calc(100vw - 18px))",
        maxWidth: "calc(100vw - 18px)",

        display: "flex",
        alignItems: "center",
        gap: 1.1,

        px: 1.1,
        py: 0.65,

        backgroundColor: "rgba(255,255,255,0.96)",
        color: "#111",
        borderRadius: "999px",
        border: "1px solid rgba(0,0,0,0.10)",
        boxShadow: "0 10px 28px rgba(0,0,0,0.18)",
        backdropFilter: "blur(10px)",
        WebkitBackdropFilter: "blur(10px)",
      }}
      role="status"
      aria-live="polite"
    >
      {/* Countdown capsule */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.8,
          px: 0.9,
          py: 0.55,
          borderRadius: "999px",
          backgroundColor: "rgba(0,0,0,0.04)",
          border: "1px solid rgba(0,0,0,0.06)",
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: "50%",
            display: "grid",
            placeItems: "center",
            backgroundColor: "rgba(0,0,0,0.05)",
            border: "1px solid rgba(0,0,0,0.08)",
          }}
        >
          {remainingMs > 0 ? (
            <ScheduleIcon sx={{ fontSize: 17 }} />
          ) : (
            <CelebrationIcon sx={{ fontSize: 17 }} />
          )}
        </Box>

        <Box sx={{ lineHeight: 1 }}>
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: 12,
              lineHeight: 1.05,
            }}
          >
            {topLabel}
          </Typography>

          <Typography
            sx={{
              fontWeight: 900,
              fontSize: 14,
              lineHeight: 1.05,
              letterSpacing: "0.4px",
            }}
          >
            {timeLabel}
          </Typography>
        </Box>
      </Box>

      {/* Message */}
      <Typography
        sx={{
          fontWeight: 650,
          fontSize: { xs: 12.5, sm: 13.5 },
          lineHeight: 1.3,
          color: "#111",
          opacity: 0.95,

          display: "-webkit-box",
          WebkitBoxOrient: "vertical",
          WebkitLineClamp: { xs: 2, sm: 1 },
          overflow: "hidden",

          flex: 1,
        }}
        title={message}
      >
        {message}
      </Typography>

      {/* Close */}
      <IconButton
        size="small"
        onClick={() => setDismissed(true)}
        aria-label="Close banner"
        sx={{
          ml: 0.2,
          width: 34,
          height: 34,
          flexShrink: 0,
          color: "rgba(0,0,0,0.7)",
          "&:hover": { backgroundColor: "rgba(0,0,0,0.06)" },
        }}
      >
        <CloseIcon sx={{ fontSize: 18 }} />
      </IconButton>
    </Box>
  );
}